import React, { useCallback, useEffect, useRef, useState } from 'react';
import ServerTestSection from '../components/HomePage/ServerTestSection';
import ServerInfoCard from '../components/Shared/ServerInfoCard';
import RecentTestedServers from '../components/HomePage/RecentTestedServers';
import ProofSection from '../components/HomePage/ProofSection';
import ServiceStats from '../components/HomePage/ServiceStats';
import ZeroDayBanner from '../components/HomePage/ZeroDayBanner';
import ServerProtection from '../components/HomePage/ServerProtection';
import AllTools from '../components/HomePage/AllTools';
import MusicStatsDev from '../components/HomePage/MusicStatsDev';
import PageTransition from '../components/Shared/PageTransition';
import { useServerConnection } from '../contexts/ServerConnectionContext';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import useWebSocket from '../hooks/useWebSocket';
import { WS_HOME_PAGE_TOPICS, WS_HOME_PAGE_TYPES } from '../config/wsHomePageConfig';

const HomePage = ({ openModal }) => {
  const { isConnected: isWebSocketConnected, sendMessage, subscribe } = useWebSocket();
  const { isAuthenticated, triggerGoogleLogin } = useAuth();
  const {
    isConnecting,
    connectToServer,
    setTargetIp
  } = useServerConnection();

  // Helper to persist recent servers to localStorage
  const saveRecentServersToCache = (servers) => {
    try {
      if (Array.isArray(servers) && servers.length > 0) {
        localStorage.setItem('stormhack_recent_servers', JSON.stringify(servers));
      }
    } catch (e) {
      // ignore
    }
  };

  // --- Home Page State ---
  const [recentServers, setRecentServers] = useState(() => {
    try {
      const cached = localStorage.getItem('stormhack_recent_servers');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed) && parsed.length > 0) {
          return parsed;
        }
      }
    } catch (e) {
      // ignore
    }
    return [];
  });
  const [recentError, setRecentError] = useState(null);
  const [proofs, setProofs] = useState([]);
  const [proofsError, setProofsError] = useState(null);
  const [isHomeDataLoading, setIsHomeDataLoading] = useState(true);
  const [hasMoreServers, setHasMoreServers] = useState(true);
  const [isLoadingMoreServers, setIsLoadingMoreServers] = useState(false);

  const { showToast } = useNotification();

  const serverInfoRef = useRef(null);
  const subscriptions = useRef({});
  const dataRequestedRef = useRef(false);
  const retryTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);

  // Pagination refs to strictly prevent duplicate or out-of-order requests
  const hasMoreServersRef = useRef(true);
  const isFetchingMoreRef = useRef(false);
  const pageRef = useRef(1); // Page 0 is fetched initially; next page is 1
  const lastFetchedPageRef = useRef(-1);

  // --- WebSocket Handlers ---
  const updateRecentServersList = useCallback((newServer) => {
    if (!newServer || !newServer.ip) return;
    setRecentServers(prevServers => {
      const normIp = newServer.ip.trim().toLowerCase();
      const filtered = prevServers.filter(s => s && s.ip && s.ip.trim().toLowerCase() !== normIp);
      const updated = [newServer, ...filtered].slice(0, 100);
      saveRecentServersToCache(updated);
      return updated;
    });
    setRecentError(null);
  }, []);

  const handleRecentServers = useCallback((message) => {
    if (!message || !message.data) return;
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    isFetchingMoreRef.current = false;
    setIsLoadingMoreServers(false);

    if (message.data.history) {
      const newItems = message.data.history;
      // If backend returned fewer than 20 items, there are no more pages in the DB!
      if (!newItems || newItems.length < 20) {
        hasMoreServersRef.current = false;
        setHasMoreServers(false);
      } else {
        // Prepare next page
        pageRef.current += 1;
      }

      setRecentServers(prev => {
        let updated;
        if (prev.length > 0 && newItems && newItems.length > 0) {
          const mergedMap = new Map();
          // Keep existing servers
          prev.forEach(item => {
            if (item && item.ip) mergedMap.set(item.ip.trim().toLowerCase(), item);
          });
          // Merge incoming page servers
          newItems.forEach(item => {
            if (item && item.ip) {
              const key = item.ip.trim().toLowerCase();
              const existing = mergedMap.get(key);
              if (!existing) {
                mergedMap.set(key, item);
              } else {
                const prevDate = new Date(existing.createdAt);
                const newDate = new Date(item.createdAt);
                if (newDate > prevDate) {
                  mergedMap.set(key, item);
                }
              }
            }
          });
          updated = Array.from(mergedMap.values())
            .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
        } else if (newItems && newItems.length > 0) {
          updated = newItems;
        } else {
          updated = prev;
        }
        saveRecentServersToCache(updated);
        return updated;
      });
      setRecentError(null);
      setIsHomeDataLoading(false);
    } else if (message.data.ip) {
      updateRecentServersList(message.data);
    }
  }, [updateRecentServersList]);

  const handleNewServerBroadcast = useCallback((message) => {
    const serverItem = message?.data && !message.data.history ? message.data : (message?.ip ? message : null);
    if (serverItem && serverItem.ip) {
      updateRecentServersList(serverItem);
    }
  }, [updateRecentServersList]);

  // Load next batch of 20 recent servers when scrolling towards end
  const loadMoreServers = useCallback(() => {
    if (isFetchingMoreRef.current || !hasMoreServersRef.current) return;
    
    // Synchronous lock to prevent multiple rapid triggers
    isFetchingMoreRef.current = true;
    setIsLoadingMoreServers(true);

    const pageToFetch = pageRef.current;
    if (pageToFetch <= lastFetchedPageRef.current) {
      isFetchingMoreRef.current = false;
      setIsLoadingMoreServers(false);
      return;
    }
    lastFetchedPageRef.current = pageToFetch;

    if (isWebSocketConnected) {
      try {
        sendMessage(WS_HOME_PAGE_TOPICS.APP_ENDPOINT_BASE + WS_HOME_PAGE_TOPICS.APP_GET_RECENT_SERVERS, {
          page: pageToFetch,
          size: 20
        });
      } catch (e) {
        console.warn("Failed to request more recent servers via WS:", e);
        isFetchingMoreRef.current = false;
        setIsLoadingMoreServers(false);
      }
    } else {
      isFetchingMoreRef.current = false;
      setIsLoadingMoreServers(false);
    }
  }, [isWebSocketConnected, sendMessage]);

  const handleHomeDataError = useCallback((message) => {
    const errorMsg = message?.error || message?.message || "Failed to load home page data.";
    setRecentError(errorMsg);
    setIsHomeDataLoading(false);
  }, []);

  const handleProofs = useCallback((message) => {
    if (message?.type === WS_HOME_PAGE_TYPES.PROOFS_LIST && message.data?.history) {
      setProofs(message.data.history);
      setProofsError(null);
    }
  }, []);

  const handleServerConnect = useCallback((ip) => {
    if (!ip) return;
    setTargetIp?.(ip);
    showToast(`Выбран сервер: ${ip}`);
    serverInfoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (!isAuthenticated) {
      triggerGoogleLogin?.();
      return;
    }
    if (!isConnecting) {
      setTimeout(() => connectToServer(ip), 100);
    }
  }, [connectToServer, isConnecting, showToast, setTargetIp, isAuthenticated, triggerGoogleLogin]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHomeDataLoading(false);
    }, 4000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isWebSocketConnected) {
      Object.values(subscriptions.current).forEach(unsub => unsub?.());
      subscriptions.current = {};
      dataRequestedRef.current = false;
      retryCountRef.current = 0;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      return;
    }

    Object.values(subscriptions.current).forEach(unsub => unsub?.());
    subscriptions.current = {};

    subscriptions.current.recentServers = subscribe(WS_HOME_PAGE_TOPICS.USER_QUEUE_BASE + WS_HOME_PAGE_TOPICS.USER_RECENT_SERVERS, handleRecentServers);
    subscriptions.current.proofs = subscribe(WS_HOME_PAGE_TOPICS.USER_QUEUE_BASE + WS_HOME_PAGE_TOPICS.USER_PROOFS, handleProofs);
    subscriptions.current.error = subscribe(WS_HOME_PAGE_TOPICS.USER_QUEUE_BASE + WS_HOME_PAGE_TOPICS.USER_ERROR, handleHomeDataError);
    subscriptions.current.newServerBroadcast = subscribe(WS_HOME_PAGE_TOPICS.PUBLIC_TOPIC_BASE + WS_HOME_PAGE_TOPICS.PUBLIC_RECENT_SERVERS, handleNewServerBroadcast);
    subscriptions.current.publicProofs = subscribe(WS_HOME_PAGE_TOPICS.PUBLIC_TOPIC_BASE + WS_HOME_PAGE_TOPICS.PUBLIC_PROOFS, handleProofs);

    const requestData = () => {
      try {
        sendMessage(WS_HOME_PAGE_TOPICS.APP_ENDPOINT_BASE + WS_HOME_PAGE_TOPICS.APP_GET_DATA, {});
      } catch (e) {
        console.error('Failed to send home get-data request:', e);
      }
    };

    // Slight delay of 120ms to allow STOMP SUBSCRIBE frames to register on the broker before request
    const initialTimer = setTimeout(() => {
      if (!dataRequestedRef.current) {
        setIsHomeDataLoading(true);
        lastFetchedPageRef.current = 0;
        pageRef.current = 1;
        hasMoreServersRef.current = true;
        setHasMoreServers(true);
        requestData();
        dataRequestedRef.current = true;
      }
    }, 120);

    // Auto-retry after 1500ms if recent servers didn't arrive
    const scheduleRetry = () => {
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = setTimeout(() => {
        if (retryCountRef.current < 2) {
          retryCountRef.current += 1;
          requestData();
          scheduleRetry();
        }
      }, 1500);
    };
    scheduleRetry();

    return () => {
      clearTimeout(initialTimer);
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
      Object.values(subscriptions.current).forEach(unsub => unsub?.());
      subscriptions.current = {};
    };
  }, [isWebSocketConnected, subscribe, sendMessage, handleRecentServers, handleNewServerBroadcast, handleHomeDataError, handleProofs]);

  return (
    <PageTransition>
      <div className="home-page-container min-h-screen text-white relative font-sans">
        <div className="relative z-10 max-w-[1280px] mx-auto px-4 sm:px-6 pt-8 sm:pt-14 pb-12 sm:pb-16">

          {/* 1. Hero & Dynamic Console Section */}
          <section id="homeSection" className="hero flex flex-col items-center mb-8 sm:mb-[38px]">
            <ServerTestSection />
            <div ref={serverInfoRef} className="w-full max-w-[760px] mx-auto">
              <ServerInfoCard className="w-full mx-auto relative z-40 mb-8" />
            </div>
          </section>

          {/* 2. Recently Tested Servers Showcase */}
          <RecentTestedServers
            servers={recentServers}
            isLoading={isHomeDataLoading && recentServers.length === 0}
            hasMore={hasMoreServers}
            isLoadingMore={isLoadingMoreServers}
            onLoadMore={loadMoreServers}
            error={recentError}
            onServerSelect={handleServerConnect}
          />

          {/* 3. ZeroDay Method Promo Banner (Gpt 6 Astra) */}
          <ZeroDayBanner />

          {/* 3.5. Statistics (5 KPI Cards) */}
          <ServiceStats />

          {/* 4. Our Tools (12-Column Grid with 8 Modules) */}
          <AllTools />

          {/* 5. Video Proofs Grid */}
          <ProofSection
            proofs={proofs}
            isLoading={proofs.length === 0}
            error={proofsError}
            onVideoClick={(title) => showToast(`Запуск видео: ${title}`)}
          />

          {/* 6. Server Protection (Balanced 2-Column Defense Card) */}
          <ServerProtection openModal={openModal} onProtectionActivated={(msg) => showToast(msg)} />

          {/* Optional Dev Audio Analytics */}
          <MusicStatsDev />
        </div>

      </div>
    </PageTransition>
  );
};

export default HomePage;